import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  EventNote as EventNoteIcon,
  ArrowBack as ArrowBackIcon, Phone as PhoneIcon, Groups as MeetingIcon,
  Email as EmailIcon, Assignment as TaskIcon, Note as NoteIcon,
  CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon, Flag as FlagIcon,
  Person as PersonIcon, TrendingUp as OpportunityIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmActivity, ActivityType, ActivityStatus, ActivityPriority, CrmContact, CrmOpportunity } from '../types/crm';

const emptyActivity = (): Partial<CrmActivity> => ({
  type: 'CALL',
  subject: '',
  date: new Date().toISOString().substring(0, 16),
  status: 'PENDING',
  priority: 'MEDIUM',
  contactId: undefined,
  opportunityId: undefined,
  notes: '',
});

const getActivityTypeIcon = (type: ActivityType) => {
  switch (type) {
    case 'CALL': return <PhoneIcon fontSize="small" color="primary" />;
    case 'MEETING': return <MeetingIcon fontSize="small" color="secondary" />;
    case 'EMAIL': return <EmailIcon fontSize="small" color="info" />;
    case 'TASK': return <TaskIcon fontSize="small" color="warning" />;
    case 'NOTE': return <NoteIcon fontSize="small" color="action" />;
    default: return <EventNoteIcon fontSize="small" />;
  }
};

const getActivityTypeLabel = (type: ActivityType, t: (key: string, fallback?: string) => string): string => {
  switch (type) {
    case 'CALL': return t('crm.activities.typeCall', 'Appel téléphonique');
    case 'MEETING': return t('crm.activities.typeMeeting', 'Réunion / RDV');
    case 'EMAIL': return t('crm.activities.typeEmail', 'Email');
    case 'TASK': return t('crm.activities.typeTask', 'Tâche');
    case 'NOTE': return t('crm.activities.typeNote', 'Note');
    default: return type;
  }
};

const getStatusColor = (status: ActivityStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'PENDING': return 'warning';
    case 'COMPLETED': return 'success';
    case 'CANCELLED': return 'default';
    default: return 'default';
  }
};

const getStatusLabel = (status: ActivityStatus, t: (key: string, fallback?: string) => string): string => {
  switch (status) {
    case 'PENDING': return t('crm.activities.statusPending', 'En attente');
    case 'COMPLETED': return t('crm.activities.statusCompleted', 'Terminé');
    case 'CANCELLED': return t('crm.activities.statusCancelled', 'Annulé');
    default: return status;
  }
};

const getPriorityColor = (priority: ActivityPriority): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (priority) {
    case 'HIGH': return 'error';
    case 'MEDIUM': return 'warning';
    case 'LOW': return 'info';
    default: return 'default';
  }
};

export const ActivitiesPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [activitiesList, setActivitiesList] = useState<CrmActivity[]>([]);
  const [contactsList, setContactsList] = useState<CrmContact[]>([]);
  const [opportunitiesList, setOpportunitiesList] = useState<CrmOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmActivity>>(emptyActivity());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [acts, contacts, opps] = await Promise.all([
        crmApi.getActivities(),
        crmApi.getContacts(),
        crmApi.getOpportunities()
      ]);
      setActivitiesList(acts);
      setContactsList(contacts);
      setOpportunitiesList(opps);
    } catch (err) {
      console.error('Error loading CRM activities:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyActivity());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (act: CrmActivity) => {
    setFormData({ ...act });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (act: CrmActivity) => {
    const actId = (act.id || act.CAC_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.activities.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette activité ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteActivity(actId);
      if (res.success) {
        blobticMessage.success(t('crm.activities.deleteSuccess', 'Activité supprimée avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleToggleCompleted = async (act: CrmActivity) => {
    const actId = (act.id || act.CAC_IDF)!;
    const nextStatus: ActivityStatus = act.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
    const res = await crmApi.updateActivity({
      CAC_IDF: actId,
      CAC_STATUS: nextStatus,
    });
    if (res.success) {
      loadData();
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.subject?.trim()) {
      return t('crm.activities.subjectRequired', 'Le sujet de l\'activité est requis.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CAC_IDF: formData.id || formData.CAC_IDF,
      CAC_CODE: formData.code || formData.CAC_CODE,
      CAC_TYPE: formData.type,
      CAC_SUBJECT: formData.subject,
      CAC_DATD: formData.date,
      CAC_STATUS: formData.status,
      CAC_PRIORITY: formData.priority,
      CCT_IDF: formData.contactId,
      COP_IDF: formData.opportunityId,
      CAC_NOTES: formData.notes,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.activities.editTitle', 'Modifier l\'Activité') : t('crm.activities.newTitle', 'Nouvelle Activité')}
        icon={<EventNoteIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_activity', primaryKey: 'CAC_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyActivity())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('crm.activities.details', 'Détails de l\'activité')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.activities.type', 'Type d\'activité *')}
                value={formData.type || 'CALL'}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as ActivityType })}
                required
              >
                <MenuItem value="CALL">{t('crm.activities.typeCall', 'Appel téléphonique')}</MenuItem>
                <MenuItem value="MEETING">{t('crm.activities.typeMeeting', 'Réunion / RDV')}</MenuItem>
                <MenuItem value="EMAIL">{t('crm.activities.typeEmail', 'Email')}</MenuItem>
                <MenuItem value="TASK">{t('crm.activities.typeTask', 'Tâche')}</MenuItem>
                <MenuItem value="NOTE">{t('crm.activities.typeNote', 'Note')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={t('crm.activities.subject', 'Sujet / Titre *')}
                value={formData.subject || ''}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label={t('crm.activities.date', 'Date et heure *')}
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.activities.status', 'Statut')}
                value={formData.status || 'PENDING'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as ActivityStatus })}
              >
                <MenuItem value="PENDING">{t('crm.activities.statusPending', 'En attente')}</MenuItem>
                <MenuItem value="COMPLETED">{t('crm.activities.statusCompleted', 'Terminé')}</MenuItem>
                <MenuItem value="CANCELLED">{t('crm.activities.statusCancelled', 'Annulé')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.activities.priority', 'Priorité')}
                value={formData.priority || 'MEDIUM'}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as ActivityPriority })}
              >
                <MenuItem value="LOW">{t('crm.activities.priorityLow', 'Basse')}</MenuItem>
                <MenuItem value="MEDIUM">{t('crm.activities.priorityMedium', 'Moyenne')}</MenuItem>
                <MenuItem value="HIGH">{t('crm.activities.priorityHigh', 'Haute')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('crm.activities.contact', 'Contact associé')}
                value={formData.contactId || ''}
                onChange={(e) => setFormData({ ...formData, contactId: e.target.value ? Number(e.target.value) : undefined })}
              >
                <MenuItem value="">{t('common.none', '-- Aucun --')}</MenuItem>
                {contactsList.map((c) => (
                  <MenuItem key={c.id || c.CCT_IDF} value={c.id || c.CCT_IDF}>
                    {c.firstName} {c.lastName} {c.company ? `(${c.company})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('crm.activities.opportunity', 'Opportunité associée')}
                value={formData.opportunityId || ''}
                onChange={(e) => setFormData({ ...formData, opportunityId: e.target.value ? Number(e.target.value) : undefined })}
              >
                <MenuItem value="">{t('common.none', '-- Aucune --')}</MenuItem>
                {opportunitiesList.map((o) => (
                  <MenuItem key={o.id || o.COP_IDF} value={o.id || o.COP_IDF}>
                    {o.title} ({(o.amount || 0).toLocaleString()} DZD)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('crm.activities.notes', 'Notes / Résumé de l\'échange')}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  // Columns for GenericTable
  const columns = [
    {
      id: 'type',
      label: t('crm.activities.type', 'Type'),
      minWidth: 140,
      format: (_: any, row: CrmActivity) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {getActivityTypeIcon(row.type)}
          <Typography variant="body2" fontWeight={600}>
            {getActivityTypeLabel(row.type, t)}
          </Typography>
        </Stack>
      )
    },
    {
      id: 'subject',
      label: t('crm.activities.subject', 'Sujet'),
      minWidth: 220,
      format: (_: any, row: CrmActivity) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ textDecoration: row.status === 'COMPLETED' ? 'line-through' : 'none' }}
          >
            {row.subject}
          </Typography>
          {row.notes && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
              {row.notes}
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'date',
      label: t('crm.activities.date', 'Date & Heure'),
      minWidth: 150,
      format: (_: any, row: CrmActivity) => (
        <Typography variant="body2" color="text.secondary">
          {row.date || '-'}
        </Typography>
      )
    },
    {
      id: 'relations',
      label: t('crm.activities.relatedTo', 'Lié à'),
      minWidth: 180,
      format: (_: any, row: CrmActivity) => (
        <Stack spacing={0.5}>
          {row.contactName && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PersonIcon fontSize="inherit" color="action" />
              <Typography variant="caption" noWrap>{row.contactName}</Typography>
            </Stack>
          )}
          {row.opportunityTitle && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <OpportunityIcon fontSize="inherit" color="primary" />
              <Typography variant="caption" color="primary" noWrap>{row.opportunityTitle}</Typography>
            </Stack>
          )}
          {!row.contactName && !row.opportunityTitle && (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
        </Stack>
      )
    },
    {
      id: 'priority',
      label: t('crm.activities.priority', 'Priorité'),
      minWidth: 100,
      format: (_: any, row: CrmActivity) => (
        <Chip
          label={t(row.priority, row.priority)}
          color={getPriorityColor(row.priority)}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      id: 'status',
      label: t('crm.activities.status', 'Statut'),
      minWidth: 120,
      format: (_: any, row: CrmActivity) => (
        <Chip
          label={getStatusLabel(row.status, t)}
          color={getStatusColor(row.status)}
          size="small"
          variant={row.status === 'COMPLETED' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
      )
    }
  ];

  return (
    <GenericPage
      title={t('crm.activities.pageTitle', 'Activités & Suivis')}
      icon={<EventNoteIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('crm.activities.new', 'Nouvelle Activité')}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {activitiesList.map((act) => (
            <Grid item xs={12} sm={6} md={4} key={act.id || act.CAC_IDF}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  borderLeft: act.status === 'COMPLETED' ? '4px solid #4caf50' : '4px solid #1976d2',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getActivityTypeIcon(act.type)}
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        {getActivityTypeLabel(act.type, t).toUpperCase()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Chip
                        label={getStatusLabel(act.status, t)}
                        color={getStatusColor(act.status)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>
                  </Stack>

                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      lineHeight: 1.3,
                      mb: 1,
                      textDecoration: act.status === 'COMPLETED' ? 'line-through' : 'none',
                      color: act.status === 'COMPLETED' ? 'text.secondary' : 'text.primary'
                    }}
                  >
                    {act.subject}
                  </Typography>

                  {act.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {act.notes}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={0.8} sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 1.5 }}>
                    {act.date && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ScheduleIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="body2">{act.date}</Typography>
                      </Stack>
                    )}
                    {act.contactName && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="body2" noWrap>{act.contactName}</Typography>
                      </Stack>
                    )}
                    {act.opportunityTitle && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <OpportunityIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="body2" noWrap>{act.opportunityTitle}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center" pt={1}>
                    <Chip
                      icon={<FlagIcon fontSize="small" />}
                      label={t(act.priority, act.priority)}
                      color={getPriorityColor(act.priority)}
                      size="small"
                      variant="outlined"
                    />

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={act.status === 'COMPLETED' ? t('crm.activities.markPending', 'Marquer en attente') : t('crm.activities.markCompleted', 'Marquer terminé')}>
                        <IconButton size="small" color={act.status === 'COMPLETED' ? 'success' : 'default'} onClick={() => handleToggleCompleted(act)}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(act)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(act)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {activitiesList.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  {t('crm.activities.empty', 'Aucune activité trouvée. Cliquez sur "Nouvelle Activité" pour planifier un appel, RDV ou tâche.')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <GenericTable
          columns={columns}
          data={activitiesList}
          filterColumns={['subject', 'notes', 'contactName', 'opportunityTitle', 'type', 'status']}
          searchPlaceholder={t('crm.activities.searchPlaceholder', 'Rechercher une activité par sujet, contact, opportunité...')}
          maxHeight="calc(100vh - 280px)"
          onEdit={openEdit}
          onDelete={handleDelete}
          rowActions={(row: CrmActivity) => (
            <Tooltip title={row.status === 'COMPLETED' ? t('crm.activities.markPending', 'Marquer en attente') : t('crm.activities.markCompleted', 'Marquer terminé')}>
              <IconButton size="small" color={row.status === 'COMPLETED' ? 'success' : 'default'} onClick={() => handleToggleCompleted(row)}>
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
      )}
    </GenericPage>
  );
};

export default ActivitiesPage;
